import React, { useMemo, useState } from "react";

import Box from "../Box";

import styled from "styled-components";
import BaseDashboardCard from "../BaseDashboardCard";
import useBreakPoint from "../../utils/useBreakPoint";
import { CPopover, CText } from "@carrefour/design-system-components-react";
import PaginationCard from "../PaginationCard";
import { Flop10 } from "../../types/drive";
import { useTranslation } from "react-i18next";

type FlopDriveCardProps = {
  listValue: Flop10[];
  isUnavailable: boolean;
  isLoading: boolean;
  openInformationModal: (title?: string) => void;
};

const Text = styled(CText)`
  font-family: system-ui;
`;
const Popover = styled(CPopover)`
  .c-popover__box {
    width: calc(100% + 80px);
  }
  > * .c-popover__header p {
    font-family: system-ui;
    font-size: small;
  }
`;
const TextEllipsis = styled(CText)`
  width: 100%;
  font-family: system-ui;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Table = styled.table`
  width: 100%;
  align-items: center;
  border-collapse: collapse;
`;
const Tr = styled.tr``;
const TrCustom = styled.tr<{ isLast: boolean }>`
  border-collapse: collapse;
  border-bottom: ${(props) => (props?.isLast ? "" : " 1px solid #f0f3f6")};
`;
const Th = styled.th<{ $textAlign?: string; $width?: string }>`
  text-align: ${(props) => props?.$textAlign};
  width: ${(props) => props?.$width};
`;
const Td = styled.td<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
  padding-block: 8px;
`;

const FlopDriveCard = ({ listValue, ...res }: FlopDriveCardProps) => {
  const isBreak = useBreakPoint();
  const [page, setPage] = useState(1);
  const n = 5;
  const totalPage = Math.ceil(listValue?.length / n);

  const filterData = useMemo(() => {
    return listValue?.filter((_, index) => {
      return index >= (page - 1) * n && index < page * n;
    });
  }, [listValue, page]);

  const nextPage = () => {
    setPage((old) => old + 1);
  };
  const prevPage = () => {
    setPage((old) => old - 1);
  };
  const lastPage = () => {
    setPage(totalPage);
  };
  const firstPage = () => {
    setPage(1);
  };

  const {t} = useTranslation();

  return (
    <BaseDashboardCard
      hasModal={true}
      title={t('ecommerce.flop_articles_manquants')}
      size={"big"}
      importedDateTime={listValue?.[0]?.importedDateTime}
      {...res}
    >
      <Box display={"flex"} justifyContent={"center"} justifyItems={"center"}>
        <Box
          bg={"white"}
          display={"flex"}
          flexDirection={"column"}
          alignItems="center"
          justifyContent={"center"}
          width="100%"
          borderRadius={1}
          py={2}
        >
          <Table style={{ width: "100%", display: "table" }}>
            <thead style={{ width: "100%", display: "table" }}>
              <Tr>
                <Th $textAlign={"left"} $width={"50%"}>
                  <Box width={["150px", "auto"]}>
                    <Text bold size={"m"} tagStyle={"p"}>
                       {t('operationnel.libelles_ean')}
                    </Text>
                  </Box>
                </Th>
                <Th $textAlign={"center"} $width={"25%"}>
                  <Box width={["70px", "auto"]}>
                    <Text bold size={"m"} tagStyle={"p"}>
                      {t('operationnel.qte')}
                    </Text>
                  </Box>
                </Th>
                <Th $textAlign={"right"} $width={"25%"}>
                  <Box width={["70px", "auto"]}>
                    <Text bold size={"m"} tagStyle={"p"}>
                      {t('operationnel.rang')}
                    </Text>
                  </Box>
                </Th>
              </Tr>
            </thead>
            <Box
              width={"100%"}
              display={"flex"}
              height={listValue?.length < 5 ? "auto" : "340px"}
              flexDirection={"column"}
            >
              <tbody style={{ width: "100%", display: "table" }}>
                {filterData &&
                  filterData?.length > 0 &&
                  filterData?.map((item, index) => {
                    return subElements(
                      item,
                      isBreak,
                      filterData?.length === index + 1 && totalPage === page
                    );
                  })}
              </tbody>
            </Box>
          </Table>
          {totalPage > 1 && (
            <Box display="flex" justifyContent={"end"} width={"90%"}>
              <PaginationCard
                current={page}
                prevPage={prevPage}
                nextPage={nextPage}
                totalPage={totalPage}
                lastPage={lastPage}
                firstPage={firstPage}
                totalItems={listValue?.length}
                currentItems={filterData?.length}
              />
            </Box>
          )}
        </Box>
      </Box>
    </BaseDashboardCard>
  );
};

function subElements(item: Flop10, isBreak: string, isLast: boolean) {
  return (
    <TrCustom key={"top-rec" + item.barcode} isLast={isLast}>
      <Td $textAlign={"left"} width={"50%"}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          width={["150px", "auto"]}
        >
          {isBreak === "phone" ? (
            <Popover
              title={item.itemDesc}
              position={"top-right"}
              buttonContent={
                <TextEllipsis bold size={"m"}>
                  {item.itemDesc}
                </TextEllipsis>
              }
            />
          ) : (
            <TextEllipsis bold size={"m"}>
              {item.itemDesc}
            </TextEllipsis>
          )}
          <Text size={"m"}>{item.barcode}</Text>
        </Box>
      </Td>
      <Td $textAlign={"center"} width={"25%"}>
        <Box width={["70px", "auto"]}>
          <Text>
            {item.quantity
              ? `${new Intl.NumberFormat("fr-FR", {
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 0,
                }).format(item.quantity)}`
              : "-"}
          </Text>
        </Box>
      </Td>
      <Td $textAlign={"right"} width={"25%"}>
        <Box width={["70px", "auto"]}>
          <Text bold>
            {item.rank
              ? `${new Intl.NumberFormat("fr-FR", {
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 0,
                }).format(item.rank)}`
              : "-"}
          </Text>
        </Box>
      </Td>
    </TrCustom>
  );
}

export default FlopDriveCard;
